import { theme } from '../../tailwind.config';
import type { TransferRecommendation } from '@api-service/recommendations';

const VIEWBOX_WIDTH = 94;
const VIEWBOX_HEIGHT = 32;
const RENDERED_WIDTH_PX = 96;
const RENDERED_HEIGHT_PX = 32;
const HORIZONTAL_INSET = 2;
const SELL_LINE_WIDTH = 2;
const BUY_LINE_WIDTH = 2.5;
// Cubic control points at a third of each x-gap approximate the comparison
// chart's `tension: 0.45` curve closely enough at this size.
const CURVE_CONTROL_RATIO = 1 / 3;

type PlayerFixtures = TransferRecommendation['in']['fixtures'];

/** Sums predicted points per gameweek, sorted by gameweek. */
const pointsPerGameweek = (fixtures: PlayerFixtures): number[] => {
  const byGameweek = new Map<number, number>();
  for (const fixture of fixtures ?? []) {
    const existing = byGameweek.get(fixture.gameweek) ?? 0;
    byGameweek.set(fixture.gameweek, existing + (fixture.predictedPoints ?? 0));
  }
  return [...byGameweek.entries()]
    .sort(([gameweekA], [gameweekB]) => gameweekA - gameweekB)
    .map(([, points]) => points);
};

const buildSmoothPath = (points: number[], scaleMax: number): string => {
  if (points.length === 0) return '';

  const usableWidth = VIEWBOX_WIDTH - HORIZONTAL_INSET * 2;
  const coordinates = points.map((value, index) => ({
    x:
      points.length > 1
        ? HORIZONTAL_INSET + (index * usableWidth) / (points.length - 1)
        : VIEWBOX_WIDTH / 2,
    y: (1 - value / scaleMax) * VIEWBOX_HEIGHT
  }));

  if (coordinates.length === 1) {
    const { y } = coordinates[0];
    return `M ${HORIZONTAL_INSET} ${y.toFixed(2)} L ${
      VIEWBOX_WIDTH - HORIZONTAL_INSET
    } ${y.toFixed(2)}`;
  }

  return coordinates.reduce((path, coordinate, index) => {
    if (index === 0) {
      return `M ${coordinate.x.toFixed(2)} ${coordinate.y.toFixed(2)}`;
    }
    const previous = coordinates[index - 1];
    const gap = coordinate.x - previous.x;
    const controlOneX = previous.x + gap * CURVE_CONTROL_RATIO;
    const controlTwoX = coordinate.x - gap * CURVE_CONTROL_RATIO;
    return (
      `${path} C ${controlOneX.toFixed(2)} ${previous.y.toFixed(2)}, ` +
      `${controlTwoX.toFixed(2)} ${coordinate.y.toFixed(2)}, ` +
      `${coordinate.x.toFixed(2)} ${coordinate.y.toFixed(2)}`
    );
  }, '');
};

type PredictedPointsSparklineProps = {
  sellFixtures: PlayerFixtures;
  buyFixtures: PlayerFixtures;
  className?: string;
};

/**
 * Minimalist two-line predicted-points graph for a transfer row: red line for
 * the player being sold, green line for the player being bought. Both lines
 * share a y scale pinned to the higher of the two series' peaks.
 */
export const PredictedPointsSparkline = ({
  sellFixtures,
  buyFixtures,
  className
}: PredictedPointsSparklineProps) => {
  const sellSeries = pointsPerGameweek(sellFixtures);
  const buySeries = pointsPerGameweek(buyFixtures);

  const highestPoints = Math.max(0, ...sellSeries, ...buySeries);
  const scaleMax = highestPoints > 0 ? highestPoints : 1;

  return (
    <svg
      width={RENDERED_WIDTH_PX}
      height={RENDERED_HEIGHT_PX}
      viewBox={`0 0 ${VIEWBOX_WIDTH} ${VIEWBOX_HEIGHT}`}
      preserveAspectRatio="none"
      className={`flex-shrink-0 overflow-visible ${className ?? ''}`}
      aria-hidden="true"
      data-cy="predicted-points-sparkline"
    >
      <path
        d={buildSmoothPath(sellSeries, scaleMax)}
        fill="none"
        stroke={theme.colors.danger[600]}
        strokeWidth={SELL_LINE_WIDTH}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
      <path
        d={buildSmoothPath(buySeries, scaleMax)}
        fill="none"
        stroke={theme.colors.success[600]}
        strokeWidth={BUY_LINE_WIDTH}
        strokeLinecap="round"
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  );
};
